/**
 * The submission / review enums, read from `value_sets_master` at runtime — the
 * same rule the definition screens follow: nothing here declares a code.
 *
 * There is deliberately NO hardcoded fallback. A stale fallback silently
 * disagrees with what the server will accept, and a verdict saved against a code
 * the server rejects fails at submit with a confusing 400. If a set is missing
 * the picker is empty and `seeded` is false, which points at the real fix — run
 * the bootstrap.
 */
import { useQuery } from '@tanstack/react-query';
import { valueSetsService } from '../../services/value-sets.service';

export interface VsOption { label: string; value: string }

/** Seeded by app/api/v1/services/feedback_bootstrap.py. */
export const EVENT_VS = {
  submissionStatus: 'feedbackSubmissionStatus',
  reviewStatus: 'feedbackReviewStatus',
  reviewOutcome: 'feedbackReviewOutcome',
  sentiment: 'feedbackSentiment',
} as const;

function useValueSet(key: string) {
  const q = useQuery({
    queryKey: ['value-set-items', key],
    queryFn: () => valueSetsService.getItems(key),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const options: VsOption[] = (q.data ?? []).map((i) => ({
    label: i.label || i.code,
    value: i.code,
  }));
  return { options, seeded: options.length > 0, loading: q.isLoading };
}

export const useSubmissionStatuses = () => useValueSet(EVENT_VS.submissionStatus);
export const useReviewStatuses = () => useValueSet(EVENT_VS.reviewStatus);
export const useReviewOutcomes = () => useValueSet(EVENT_VS.reviewOutcome);
export const useSentiments = () => useValueSet(EVENT_VS.sentiment);
